import { Select } from "antd";
import { ReactNode, useContext, useId, useMemo } from "react";
import { HomeContext } from "../../containers/Home";
import { useProjectOptions } from "../../hooks/useDashboardQueries";
import CheckboxDropdown from "../CheckboxDropdown";

interface Option<T extends string | number> {
	value: T;
	label: string;
}

interface FilterFieldProps {
	label: string;
	/** The control's id; the CheckboxDropdown has none and names itself. */
	htmlFor?: string;
	help?: string;
	children: ReactNode;
}

function FilterField({ label, htmlFor, help, children }: FilterFieldProps) {
	return (
		<div className="dashboard-expanded__field">
			{htmlFor ? (
				<label htmlFor={htmlFor}>{label}</label>
			) : (
				<span>{label}</span>
			)}
			{children}
			{help && <span className="dashboard-expanded__help">{help}</span>}
		</div>
	);
}

interface SelectFieldProps<T extends string | number> {
	label: string;
	value: T;
	options: Option<T>[];
	onChange: (value: T) => void;
}

/** A labelled single select that always has a value. */
export function SelectField<T extends string | number>({
	label,
	value,
	options,
	onChange,
}: SelectFieldProps<T>) {
	const id = useId();
	return (
		<FilterField label={label} htmlFor={id}>
			<Select<T>
				id={id}
				value={value}
				options={options}
				onChange={onChange}
				popupMatchSelectWidth={false}
			/>
		</FilterField>
	);
}

interface TenantFieldProps {
	options: Option<string>[];
	value: string;
	onChange: (value: string) => void;
}

/** The tenant filter, left out when there is only one tenant to show. */
export function TenantField({ options, value, onChange }: TenantFieldProps) {
	if (options.length < 2) return null;
	return (
		<SelectField
			label="Tenant"
			value={value}
			options={options}
			onChange={onChange}
		/>
	);
}

interface OptionalSelectFieldProps<T extends string | number> {
	label: string;
	value: T | undefined;
	options: Option<T>[];
	onChange: (value: T | undefined) => void;
	loading?: boolean;
	help?: string;
}

/** A labelled, searchable single select that can be cleared ("Any"). */
function OptionalSelectField<T extends string | number>({
	label,
	value,
	options,
	onChange,
	loading = false,
	help,
}: OptionalSelectFieldProps<T>) {
	const id = useId();
	return (
		<FilterField label={label} htmlFor={id} help={help}>
			<Select<T>
				id={id}
				value={value}
				options={options}
				onChange={(next) => onChange(next ?? undefined)}
				placeholder="Any"
				allowClear
				showSearch
				optionFilterProp="label"
				loading={loading}
				popupMatchSelectWidth={false}
			/>
		</FilterField>
	);
}

interface MultiSelectFieldProps {
	label: string;
	options: Option<string>[];
	values: string[];
	onChange: (values: string[]) => void;
}

/** A labelled multi-select; no selection means every option. */
export function MultiSelectField({
	label,
	options,
	values,
	onChange,
}: MultiSelectFieldProps) {
	return (
		<FilterField label={label}>
			<div role="group" aria-label={label}>
				<CheckboxDropdown
					Key={label}
					label={values.length ? "Selected" : "All"}
					options={options}
					defaultValue={values}
					onChange={(selected) => onChange([...selected])}
					onClear={() => onChange([])}
				/>
			</div>
		</FilterField>
	);
}

interface ProjectFieldProps {
	tenantKey: string;
	value: string | undefined;
	onChange: (value: string | undefined) => void;
	help?: string;
}

/** The non-removed projects of one tenant. */
export function ProjectField({
	tenantKey,
	value,
	onChange,
	help,
}: ProjectFieldProps) {
	const { data = [], isLoading } = useProjectOptions(tenantKey);
	return (
		<OptionalSelectField
			label="Project"
			value={value}
			options={data}
			onChange={onChange}
			loading={isLoading}
			help={help}
		/>
	);
}

interface LookupFieldProps {
	label: string;
	/** Key of the lookup in Home's `allData`. */
	lookup: "discipline" | "lesson_learned_type";
	value: string | undefined;
	onChange: (value: string | undefined) => void;
}

/** A global lookup that Home has already loaded. */
export function LookupField({
	label,
	lookup,
	value,
	onChange,
}: LookupFieldProps) {
	const { allData }: any = useContext(HomeContext);
	const items = allData?.[lookup];
	const options = useMemo(
		() =>
			(Array.isArray(items) ? items : [])
				.filter((item: any) => item?._id && item[lookup])
				.map((item: any) => ({
					value: String(item._id),
					label: String(item[lookup]),
				})),
		[items, lookup]
	);
	return (
		<OptionalSelectField
			label={label}
			value={value}
			options={options}
			onChange={onChange}
			loading={!allData}
		/>
	);
}
